import { fuelType } from './fuelType';

export type EmissionsConsumptionPhaseValues =
  | 'combined'
  | 'extraHigh'
  | 'high'
  | 'medium'
  | 'low'
  | 'batteryRangeCombined'
  | 'batteryRangeCity'
  | 'combinedSustaining'
  | 'energyCombined';

export type EmissionsConsumptionValueTypes = 'consumption' | 'emissions';

// CONSUMPTION
const consumptionPetrolDieselSharedValues = {
  attributeType: 'WLTP',
  valueType: 'CONSUMPTION',
  energyManagementType: 'PURE',
};

const consumptionHybridSharedValues = {
  attributeType: 'WLTP',
  valueType: 'CONSUMPTION',
  energyManagementType: 'SUSTAINING',
  fuelType: 'PETROL',
};

const consumptionCombined = {
  ...consumptionPetrolDieselSharedValues,
  phase: 'COMBINED',
};

const consumptionExtraHigh = {
  ...consumptionPetrolDieselSharedValues,
  phase: 'EXTRA_HIGH',
};

const consumptionHigh = {
  ...consumptionPetrolDieselSharedValues,
  phase: 'HIGH',
};

const consumptionMedium = {
  ...consumptionPetrolDieselSharedValues,
  phase: 'MEDIUM',
};

const consumptionLow = {
  ...consumptionPetrolDieselSharedValues,
  phase: 'LOW',
};

const electricBatteryRange = {
  attributeType: 'WLTP',
  valueType: 'RANGE',
  phase: 'COMBINED',
  energyManagementType: 'PURE',
};

const electricBatteryRangeCity = {
  attributeType: 'WLTP',
  valueType: 'RANGE',
  phase: 'CITY',
  energyManagementType: 'PURE',
};

const electricBatteryRangeCityPluginHybrid = {
  attributeType: 'WLTP',
  valueType: 'RANGE',
  phase: 'CITY',
  energyManagementType: 'EQUIV_ALL_EL_RANGE',
  fuelType: 'ELECTRICAL',
};

const consumptionCombinedHybridWeighted = {
  attributeType: 'WLTP',
  valueType: 'CONSUMPTION',
  phase: 'COMBINED',
  energyManagementType: 'WEIGHTED',
  fuelType: 'PETROL',
};

const energyCombinedHybridWeighted = {
  attributeType: 'WLTP',
  valueType: 'CONSUMPTION',
  phase: 'COMBINED',
  energyManagementType: 'WEIGHTED',
  fuelType: 'ELECTRICAL',
};

const electricRangeCombinedPluginHybrid = {
  attributeType: 'WLTP',
  valueType: 'RANGE',
  phase: 'COMBINED',
  energyManagementType: 'EQUIV_ALL_EL_RANGE',
  fuelType: 'ELECTRICAL',
};

const consumptionCombinedHybridSustaining = {
  ...consumptionHybridSharedValues,
  phase: 'COMBINED',
};

const consumptionExtraHighHybrid = {
  ...consumptionHybridSharedValues,
  phase: 'EXTRA_HIGH',
};

const consumptionHighHybrid = {
  ...consumptionHybridSharedValues,
  phase: 'HIGH',
};

const consumptionMediumHybrid = {
  ...consumptionHybridSharedValues,
  phase: 'MEDIUM',
};

const consumptionLowHybrid = {
  ...consumptionHybridSharedValues,
  phase: 'LOW',
};

// EMISSIONS

const emissionsCombinedSharedValues = {
  attributeType: 'WLTP',
  valueType: 'CO2',
  phase: 'COMBINED',
};

const emissionsCombined = {
  ...emissionsCombinedSharedValues,
  energyManagementType: 'PURE',
};

const emissionsCombinedHybridWeighted = {
  ...emissionsCombinedSharedValues,
  energyManagementType: 'WEIGHTED',
};

const emissionsCombinedHybridSustaining = {
  ...emissionsCombinedSharedValues,
  energyManagementType: 'SUSTAINING',
};

const petrolDieselConsumptionModels = {
  combined: {
    ...consumptionCombined,
  },
  extraHigh: {
    ...consumptionExtraHigh,
  },
  high: {
    ...consumptionHigh,
  },
  medium: {
    ...consumptionMedium,
  },
  low: {
    ...consumptionLow,
  },
};

const electricConsumptionModels = {
  combined: {
    ...consumptionCombined,
    fuelType: 'ELECTRICAL',
  },
  batteryRangeCombined: {
    ...electricBatteryRange,
    fuelType: 'ELECTRICAL',
  },
  batteryRangeCity: {
    ...electricBatteryRangeCity,
    fuelType: 'ELECTRICAL',
  },
  high: {
    ...consumptionHigh,
    fuelType: 'ELECTRICAL',
  },
  medium: {
    ...consumptionMedium,
    fuelType: 'ELECTRICAL',
  },
  low: {
    ...consumptionLow,
    fuelType: 'ELECTRICAL',
  },
  extraHigh: {
    ...consumptionExtraHigh,
    fuelType: 'ELECTRICAL',
  },
};

const plugInHybridConsumptionModels = {
  combined: {
    ...consumptionCombinedHybridWeighted,
  },
  combinedSustaining: {
    ...consumptionCombinedHybridSustaining,
  },
  energyCombined: {
    ...energyCombinedHybridWeighted,
  },
  extraHigh: {
    ...consumptionExtraHighHybrid,
  },
  high: {
    ...consumptionHighHybrid,
  },
  medium: {
    ...consumptionMediumHybrid,
  },
  low: {
    ...consumptionLowHybrid,
  },
  batteryRangeCombined: {
    ...electricRangeCombinedPluginHybrid,
  },
  batteryRangeCity: {
    ...electricBatteryRangeCityPluginHybrid,
  },
};

const petrolDieselEmissionsModels = {
  combined: {
    ...emissionsCombined,
  },
};

const electricEmissionsModels = {
  combined: {
    ...emissionsCombined,
    fuelType: 'ELECTRICAL',
  },
};

const plugInHybridEmissionsModels = {
  combined: {
    ...emissionsCombinedHybridWeighted,
  },
  combinedSustaining: {
    ...emissionsCombinedHybridSustaining,
  },
};

export default {
  consumption: {
    [fuelType.PETROL]: petrolDieselConsumptionModels,
    [fuelType.DIESEL]: petrolDieselConsumptionModels,
    [fuelType.HYBRID]: petrolDieselConsumptionModels,
    [fuelType.ELECTRIC]: electricConsumptionModels,
    [fuelType.PLUG_IN_HYBRID]: plugInHybridConsumptionModels,
  },
  emissions: {
    [fuelType.PETROL]: petrolDieselEmissionsModels,
    [fuelType.DIESEL]: petrolDieselEmissionsModels,
    [fuelType.HYBRID]: petrolDieselEmissionsModels,
    [fuelType.ELECTRIC]: electricEmissionsModels,
    [fuelType.PLUG_IN_HYBRID]: plugInHybridEmissionsModels,
  },
} as any;
